<template>
  <!--
    What a player says they played, as a chip in the sheet's toolbar: the pen
    alone until something is written, then the words beside it. Tapping it opens
    the line.
  -->
  <label v-if="editing" class="note-chip is-editing">
    <span class="note-label">{{ localize('handshake.note.lead') }}</span>
    <input
      ref="input"
      :value="modelValue"
      type="text"
      :maxlength="HANDSHAKE_NOTE_MAX_LENGTH"
      :placeholder="hint || localize('handshake.note.placeholder')"
      autocomplete="off"
      spellcheck="false"
      enterkeyhint="done"
      class="note-input"
      @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
      @change="emit('change')"
      @keydown.enter="($event.target as HTMLInputElement).blur()"
      @blur="editing = false"
    />
  </label>
  <button
    v-else
    type="button"
    class="note-chip"
    :class="{ 'is-icon': !shown }"
    :aria-label="localize('handshake.note.lead')"
    @click="startEditing"
  >
    <GamIcon :name="GamIconName.EDIT" size="14px" is-current />
    <span v-if="shown" class="note-text" :class="{ 'is-theirs': !modelValue }">{{ shown }}</span>
  </button>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { HANDSHAKE_NOTE_MAX_LENGTH } from '@/views/main/handshakes/utils/handshake-note';
import { computed, nextTick, ref, useTemplateRef } from 'vue';

const props = defineProps<{
  modelValue: string;
  /** What the other player wrote. Shown until this player writes their own. */
  hint?: string;
}>();

const shown = computed(() => props.modelValue || props.hint || '');

/**
 * `change` rather than every keystroke: a note is saved once it is finished
 * with, which is what leaving the line means.
 */
const emit = defineEmits<{ 'update:modelValue': [note: string]; change: [] }>();

const editing = ref(false);
const input = useTemplateRef<HTMLInputElement>('input');

const startEditing = async () => {
  editing.value = true;
  await nextTick();
  input.value?.focus();
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

/* The same pill as the clock chip in the recommendation's toolbar. */
.note-chip {
  display: flex;
  align-items: center;
  gap: var(--spacing-s);
  min-width: 0;
  max-width: 60vw;
  height: 44px;
  padding: 0 var(--spacing-l);
  border: 0;
  border-radius: 44px;
  background: var(--color-dark-500);
  color: var(--color-white);
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--transition);

  &:not(.is-editing) {
    @include hover {
      background: linear-gradient(var(--color-white-8), var(--color-white-8)), var(--color-dark-500);
    }

    @include press;
  }

  &.is-editing {
    cursor: text;
  }

  &.is-icon {
    justify-content: center;
    width: 44px;
    padding: 0;
    color: var(--color-white-70);
  }
}

.note-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.is-theirs {
    color: var(--color-white-70);
  }
}

.note-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  white-space: nowrap;
  color: var(--color-white-40);
}

.note-input {
  min-width: 0;
  width: 12ch;
  padding: 0;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-white);
  background: transparent;
  border: none;
  outline: none;
  caret-color: var(--color-white);

  &::placeholder {
    color: var(--color-white-30);
    font-weight: 500;
  }
}
</style>
